
import { attributes, AttributesMixin, Collection, define, Model } from '@type-r/models';
import { restfulIO } from "api/endpoint";
import { MultiSelectFilter } from 'lqx-react';
import { Dictionary } from 'lodash';

export class FiscalQuarter extends attributes({
    quarter: String, // TODO: add type
    startDate: Date,
    endDate: Date
}) {}

export class FiscalMonth extends attributes({
    month: String, // TODO: add type
    startDate: Date,
    endDate: Date
}) {}

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'] as const;
export type Month = typeof months[number];

const quarters = ['Q1', 'Q2', 'Q3', 'Q4'] as const;
export type Quarter = typeof quarters[number];

export interface FiscalCalendar extends AttributesMixin<typeof FiscalCalendar> {}

@define
export class FiscalCalendar extends Model {
    static endpoint = restfulIO( '/api/inblock/tradetalk/v1/FiscalCalendar/all' );

    static attributes = {
        isDefault: Boolean,
        startDate: Date,
        endDate: Date,
        months: [FiscalMonth],
        quarters: [FiscalQuarter],
        companyIds: Array
    }
}

export interface FiscalCalendarDto
{
    range: DateRange;
    months: { [month in Month]: DateRange },
    quarters: { [quarter in Quarter]: DateRange }
}

export interface DateRange
{
    start: Date;
    end: Date;
}

export const filterByFiscalPeriod = (filter: MultiSelectFilter, valueDate: Date, supplierId: number, fiscalCalendarMap: Dictionary<FiscalCalendarDto[]>, periodType: 'months' | 'quarters') : boolean => {
    if(!filter || !filter.selected || !filter.selected.length) {
        return true;
    }

    const calendars = fiscalCalendarMap[supplierId] || fiscalCalendarMap['default'];

    return calendars.some(calendar => filter.selected.some(period => calendar[periodType][period.toString()] 
        && valueDate >= calendar[periodType][period.toString()].start 
        && valueDate <= calendar[periodType][period.toString()].end));
}

export const filterByFiscalYear =  (filter: MultiSelectFilter, valueDate: Date, supplierId: number, fiscalCalendarMap: Dictionary<FiscalCalendarDto[]>) : boolean => {
    if(!filter || !filter.selected || !filter.selected.length) {
        return true;
    }

    const calendars = fiscalCalendarMap[supplierId] || fiscalCalendarMap['default'];

    const filteredYears = calendars.filter(calendar => filter.selected.includes(calendar.range.start.getFullYear().toString()));

    return filteredYears.some(calendar => valueDate >= calendar.range.start && valueDate <= calendar.range.end);
}

export const generateYearOptions = () => {
    const today = new Date();
    const year = today.getFullYear();

    return [year - 1, year, year + 1];
}

export const generateQuarterOptions = () => {
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();
    const quarter = Math.floor(month / 3);

    return [
        getQuarterName(quarter - 2, year), getQuarterName(quarter - 1, year),
        getQuarterName(quarter, year),
        getQuarterName(quarter + 1, year), getQuarterName(quarter + 2, year)
    ];
}

export const generateMonthOptions = () => {
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();

    return [
        getMonthName(month - 3, year), getMonthName(month - 2, year), getMonthName(month - 1, year),
        getMonthName(month, year),
        getMonthName(month + 1, year), getMonthName(month + 2, year), getMonthName(month + 3, year)
    ];
}

const getMonthName = (month: number, year: number) => getPeriodName(month, year, months);
const getQuarterName = (quarter: number, year: number) => getPeriodName(quarter, year, quarters);

// works only +- 1 year
const getPeriodName = (period: number, year: number, periods: readonly string[]) => {
    if (period < 0) {
        period += periods.length;
        year--;
    }
    if (period > periods.length - 1) {
        period -= periods.length;
        year++;
    }

    return periods[period] + ' ' + year;
}

export const createFiscalCalendarMap = (fiscalCalendars: Collection<FiscalCalendar>) => {
    const calendarMap: Dictionary<FiscalCalendarDto[]> = {};
    calendarMap['default'] = [];

    fiscalCalendars.forEach(calendar => {
        const months: Dictionary<DateRange> = {};
        calendar.months.forEach(month => {
            months[month.month] = { start: month.startDate, end: month.endDate };
        });

        const quarters: Dictionary<DateRange> = {};
        calendar.quarters.forEach(quarter => {
            quarters[quarter.quarter] = { start: quarter.startDate, end: quarter.endDate };
        });

        const calendarDto = {
            isDefault: calendar.isDefault,
            range: { start: calendar.startDate, end: calendar.endDate },
            months: months as any,
            quarters: quarters as any
        };

        calendar.companyIds.forEach((companyId: number) => {
            if (!calendarMap[companyId]) {
                calendarMap[companyId] = [];
            }

            calendarMap[companyId].push(calendarDto);
        });

        if (calendar.isDefault) {
            calendarMap['default'].push(calendarDto);
        }
    });

    return calendarMap;
}
